import React, { useState, useEffect } from "react";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import {
  useForm,
  SubmitHandler,
  Controller,
  useWatch,
} from "react-hook-form";
import { StylesConfig } from "react-select";
import DurationSelect from "app/components/inputs/DurationSelect";
import CloseIcon from "../../../../../../public/images/close.svg";
import { useTranslation } from "react-i18next";

import styles from "./styles.module.scss";
import ButtonVariant from "app/components/Button/ButtonVariant";
import TextInput from "app/components/inputs/TextInput";
import CheckBox from "app/components/inputs/CheckBox";
import CalendarColorPicker from "app/components/inputs/CalendarColorPicker";
import { toPayloadColor } from "app/utils/calendarColors";
import { LessonRatePayload } from "app/utils/request/scheduling/facilityLessonRates";
import PayMethodSelector, {
  PaymentMethodValue,
  DEFAULT_PAYMENT_VALUE,
  toPaymentPayload,
} from "app/components/PayMethodSelector";
import { resourceIdsPayload } from "app/components/ResourceSelector";
import ResourcesBlock from "app/components/ResourceSelector/ResourcesBlock";
import {
  ScheduleResourceResponse,
  ResourceCategoryResponse,
} from "app/utils/request/scheduling/facilityResources";
import {
  ResourceSelection,
  EMPTY_RESOURCE_SELECTION,
} from "app/components/ResourceSelector/selection";


interface FormValues {
  title: string;
  duration: string;
  rate: number | string;
  location: string;
  description: string;
  capacity: number | string;
  defaultRate: boolean;
  paymentMethod: PaymentMethodValue;
  bookingsLimit: number | string;
  hidePrice: boolean;
  color: string | null;
}

interface AddBookingOptionProps {
  isOpen: boolean;
  onClose: () => void;
  onAddBooking: (data: LessonRatePayload) => Promise<void>;
  existingTitles: string[];
  currencySymbol: string;
  showBookingsLimit?: boolean;
  stripeIntegrationEnabled?: boolean;
  resources?: ScheduleResourceResponse[];
  categories?: ResourceCategoryResponse[];
  resourcesEnabled?: boolean;
}

const DEFAULT_FORM_VALUES: FormValues = {
  title: "",
  duration: "",
  rate: 0,
  location: "",
  description: "",
  capacity: 1,
  defaultRate: false,
  paymentMethod: DEFAULT_PAYMENT_VALUE,
  bookingsLimit: "",
  hidePrice: false,
  color: null,
};

export default function AddBookingOption({
  isOpen,
  onClose,
  onAddBooking,
  existingTitles,
  currencySymbol,
  showBookingsLimit = false,
  stripeIntegrationEnabled,
  resources = [],
  categories = [],
  resourcesEnabled = false,
}: AddBookingOptionProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    control,
    trigger,
  } = useForm<FormValues>({ defaultValues: DEFAULT_FORM_VALUES });
  const { t } = useTranslation();

  // CN-2711: hiding the price only makes sense when the athlete never reaches
  // a checkout, so the option has to be collected offline and nothing else.
  const paymentMethod = useWatch({ control, name: "paymentMethod" });
  const hidePrice = useWatch({ control, name: "hidePrice" });
  const canHidePrice = (paymentMethod?.collectType ?? "offline") === "offline";

  useEffect(() => {
    if (!canHidePrice && hidePrice) {
      setValue("hidePrice", false);
    }
  }, [canHidePrice, hidePrice, setValue]);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [durationError, setDurationError] = useState<string | null>(null);
  const [showPayments, setShowPayments] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [capacityError, setCapacityError] = useState<string | null>(null);
  const [resourceSelection, setResourceSelection] = useState<ResourceSelection>(
    EMPTY_RESOURCE_SELECTION
  );

  const isTitleUnique = (title: string): boolean =>
    Array.isArray(existingTitles) &&
    existingTitles.some(
      (existing) => existing.toLowerCase() === title.toLowerCase()
    );

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setServerErrors({});
    setCapacityError(null);

    if (isTitleUnique(data.title)) {
      setTitleError(t("Title must be unique"));
      return;
    }
    if (data.duration === "") {
      setDurationError(t("This field is required"));
      return;
    }

    const capacityVal = data.capacity !== "" ? Number(data.capacity) : 1;
    if (capacityVal < 1) {
      setCapacityError(t("Must be at least 1"));
      return;
    }

    const { collect_type, online_default } = toPaymentPayload(data.paymentMethod);
    const pickedColor = toPayloadColor(data.color);

    const payload: LessonRatePayload = {
      title: data.title,
      duration: data.duration,
      rate: data.rate !== "" ? Number(data.rate) : 0,
      location: data.location || "",
      description: data.description?.trim() || null,
      capacity: capacityVal,
      default_rate: data.defaultRate,
      collect_type,
      online_default,
      bookings_limit:
        data.bookingsLimit !== "" ? Number(data.bookingsLimit) : null,
      // CN-2711: only send the flag when it actually asks for something. A
      // booking service that predates the column answers 500 to any payload
      // carrying hide_price, so an unrelated edit must not start failing
      // against an environment where this feature has not shipped yet.
      ...(data.hidePrice ? { hide_price: true } : {}),
      // Calendar colour coding takes the same guard: only send it once staff
      // have picked one.
      ...(pickedColor ? { color: pickedColor } : {}),
      ...resourceIdsPayload(
        resourcesEnabled,
        resourceSelection.scheduleResourceIds,
        resourceSelection.resourceCategoryIds
      ),
    };

    try {
      await onAddBooking(payload);
      reset(DEFAULT_FORM_VALUES);
      setResourceSelection(EMPTY_RESOURCE_SELECTION);
      setTitleError(null);
      setDurationError(null);
      setShowPayments(false);
    } catch (err) {
      const error = err as any;
      if (error?.errors) {
        const fieldErrors: Record<string, string> = {};
        Object.keys(error.errors).forEach((key) => {
          const messages = error.errors[key];
          if (Array.isArray(messages) && messages.length > 0) {
            const camelKey = key.replace(/_([a-z])/g, (_: string, letter: string) => letter.toUpperCase());
            fieldErrors[camelKey] = messages[0].charAt(0).toUpperCase() + messages[0].slice(1);
          }
        });
        setServerErrors(fieldErrors);
      }
    }
  };

  const onCancel = () => {
    onClose();
    reset(DEFAULT_FORM_VALUES);
    setResourceSelection(EMPTY_RESOURCE_SELECTION);
    setShowPayments(false);
    setTitleError(null);
    setDurationError(null);
    setServerErrors({});
    setCapacityError(null);
  };

  const customStyles: StylesConfig = {
    control: (base: any) => ({
      ...base,
      background: "white",
      border: "1px solid #ccc",
    }),
    multiValue: (styles: any) => ({
      ...styles,
      backgroundColor: "#27a4ff",
      padding: "4px",
      borderRadius: "4px",
    }),
    multiValueLabel: (styles: any) => ({
      ...styles,
      color: "white",
      fontSize: "16px",
    }),
    multiValueRemove: (styles: any) => ({
      ...styles,
      color: "white",
      ":hover": {
        color: "white",
      },
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: "black",
    }),
    container: (provided: any) => ({
      ...provided,
      color: "#8C8C8C",
    }),
    placeholder: (defaultStyles: any) => ({
      ...defaultStyles,
      color: "#8c8c8c",
    }),
  };

  const durationCustomStyles: StylesConfig = {
    ...customStyles,
    valueContainer: (base: any) => ({
      ...base,
      height: "42px",
      display: "flex",
      alignItems: "center",
    }),
  };

  useEffect(() => {
    register("duration", { required: true });
  }, [register]);

  return (
    <ModalLayout isOpen={isOpen} onClose={onClose} classNames={styles.modal}>
      <div className={styles.modal_header}>
        <h2>{t("Add Booking")}</h2>
        <img
          className={styles.modal_status_icon}
          src={CloseIcon}
          alt="close"
          onClick={onCancel}
        />
      </div>
      <div className={styles.modal_content}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.form_row}>
            {errors.title && <span>{t("This field is required")}</span>}
            {titleError && <span>{titleError}</span>}
            {serverErrors.title && (
              <span style={{ color: "red" }}>{serverErrors.title}</span>
            )}
            <TextInput
              label={`${t("Title")}*`}
              placeholder={t("e.g. 1 Hour Adult Lesson")}
              {...register("title", { required: true })}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTitleError(null);
                setServerErrors((prev) => {
                  const next = { ...prev };
                  delete next.title;
                  return next;
                });
                register("title", { required: true }).onChange(e);
              }}
            />
          </div>

          <div className={styles.form_row}>
            <label htmlFor="duration">{t("Duration")}*</label>
            <DurationSelect
              control={control}
              trigger={trigger}
              styles={durationCustomStyles}
              onChanged={() => setDurationError(null)}
            />
            {(errors.duration || durationError) && (
              <span>{t("This field is required")}</span>
            )}
          </div>

          <div className={styles.form_row}>
            <label htmlFor="rate">{t("Rate")}</label>
            <div className={styles.input_with_dollar}>
              <span className={styles.dollar_sign}>{currencySymbol}</span>
              <input
                id="rate"
                type="number"
                {...register("rate")}
                placeholder="0"
                onKeyPress={(e) => {
                  if (e.key === "-" || e.key === "e" || e.key === "+") {
                    e.preventDefault();
                  }
                }}
                onBlur={(e) => {
                  const value = parseFloat(e.target.value);
                  if (value < 0 || isNaN(value)) {
                    setValue("rate", 0);
                    trigger("rate");
                  }
                }}
              />
            </div>
          </div>

          {/* CN-2711: the rate is still charged and reported, it just stops
              being shown to whoever is booking. Only offered when the option is
              collected offline only — any online payment means a checkout, and
              a checkout cannot hide what it charges. */}
          {canHidePrice && (
            <div className={styles.form_row_checkbox}>
              <Controller
                name="hidePrice"
                control={control}
                render={({ field }) => (
                  <CheckBox
                    name="hidePrice"
                    label={t("Hide price?")}
                    checked={!!field.value}
                    onChange={field.onChange}
                    squareCheckIcons
                    testId="hide-price-checkbox"
                  />
                )}
              />
            </div>
          )}

          <div className={styles.form_row}>
            <TextInput
              label={t("Location")}
              placeholder={t("Where the session takes place")}
              {...register("location")}
            />
          </div>

          <div className={styles.form_row}>
            <label htmlFor="description">
              {t("Description (shown on your landing page)")}
            </label>
            <textarea
              id="description"
              className={styles.textarea}
              {...register("description", { maxLength: 1000 })}
              placeholder={t("What this session covers, for whoever is booking it")}
              maxLength={1000}
              rows={3}
            />
          </div>


          {showBookingsLimit && (
            <div>
              <div className={styles.form_row}>
                <label htmlFor="capacity">{t("Capacity")}</label>
                <input
                  id="capacity"
                  type="number"
                  {...register("capacity")}
                  placeholder="1"
                  onKeyPress={(e) => {
                    if (["-", "e", "+", ".", ","].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onBlur={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (!isNaN(value) && value < 1) {
                      setCapacityError(t("Must be at least 1"));
                    } else {
                      setCapacityError(null);
                    }
                  }}
                  onChange={(e) => {
                    setCapacityError(null);
                    setServerErrors((prev) => {
                      const next = { ...prev };
                      delete next.capacity;
                      return next;
                    });
                    register("capacity").onChange(e);
                  }}
                />
              </div>
              {(capacityError || serverErrors.capacity) && (
                <span style={{ color: "red", fontSize: "14px" }}>
                  {capacityError || serverErrors.capacity}
                </span>
              )}
            </div>
          )}

          {showBookingsLimit && (
            <div className={styles.form_row}>
              <label htmlFor="bookingsLimit">{t("Bookings Limit")}</label>
              <input
                id="bookingsLimit"
                type="number"
                {...register("bookingsLimit", {
                  min: { value: 1, message: t("Must be at least 1") },
                })}
                placeholder={t("Unlimited")}
                onKeyPress={(e) => {
                  if (e.key === "-" || e.key === "e" || e.key === "+" || e.key === ".") {
                    e.preventDefault();
                  }
                }}
                onBlur={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (value < 1) {
                    setValue("bookingsLimit", "");
                    trigger("bookingsLimit");
                  }
                }}
              />
              <span className={styles.field_hint}>
                {t("Maximum number of future sessions an athlete can book")}
              </span>
              {errors.bookingsLimit && (
                <span style={{ color: "red" }}>{errors.bookingsLimit.message}</span>
              )}
              {serverErrors.bookingsLimit && (
                <span style={{ color: "red" }}>{serverErrors.bookingsLimit}</span>
              )}
            </div>
          )}

          <div className={styles.form_row}>
            <Controller
              name="color"
              control={control}
              render={({ field }) => (
                <CalendarColorPicker
                  label={t("Calendar colour")}
                  hint={t("Sessions booked with this option appear in this colour on the calendar.")}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          <div className={styles.paymentDropdown}>
            <div
              className={styles.paymentToggle}
              onClick={() => setShowPayments(!showPayments)}
            >
              <div>
                <div className={styles.paymentsTitle}>{t("Payments")}</div>
                {!showPayments && (
                  <div className={styles.paymentSummary}>
                    {t("Online and Offline")}
                  </div>
                )}
              </div>
              <i
                className={`ico ico-chevron ${showPayments ? styles.open : ""}`}
              />
            </div>

            {showPayments && (
              <div className={styles.paymentsContent}>
                <p className={styles.subtext}>
                  {t(
                    "Please choose your preferred method for collecting payments for this booking option."
                  )}
                </p>

                <Controller
                  name="paymentMethod"
                  control={control}
                  render={({ field }) => (
                    <PayMethodSelector
                      value={field.value}
                      onChange={field.onChange}
                      showPackageOnly
                      disableOnline={!stripeIntegrationEnabled}
                      disabledReason={!stripeIntegrationEnabled ? t("Connect Stripe to enable online payments") : undefined}
                    />
                  )}
                />
              </div>
            )}
          </div>

          {resourcesEnabled && (
            <ResourcesBlock
              resources={resources}
              categories={categories}
              value={resourceSelection}
              onChange={setResourceSelection}
              blockStyles={styles}
            />
          )}

          <div className={styles.buttonGroup}>
            <ButtonVariant
              title={t("Cancel")}
              buttonType="secondary-outline"
              buttonStyles={styles.cancel_button}
              onClickFunc={onCancel}
            />
            <ButtonVariant
              title={t("Confirm")}
              buttonType="primary"
              buttonStyles={styles.confirm_button}
              onClickFunc={handleSubmit(onSubmit)}
            />
          </div>
        </form>
      </div>
    </ModalLayout>
  );
}
